import {
  getAddressCoordinates,
  type AddressCoordinates,
  type StoredCoordinates,
} from '@payaca/helpers/locationHelper';
import { readableProjectIntegrationService } from '@payaca/helpers/projectIntegrationsHelper';
import type { ProjectIntegrationService } from '@payaca/types/projectIntegrationTypes';
import getDistance from 'geolib/es/getDistance';

/** The integrations currently receive coordinates only when their project is created. */
export const CREATE_ONLY_DESIGN_SERVICES = [
  'OPEN_SOLAR',
  'EASY_PV',
  'HEATPUNK',
  'SPRUCE',
] as const satisfies readonly ProjectIntegrationService[];

export type AddressCoordinatePair = StoredCoordinates | null | undefined;

/** Do not show two markers when they represent the same building. */
export const LOOKUP_MARKER_COLOCATION_TOLERANCE_METRES = 5;

export type SiteLocationEditorState = {
  addressId: string | null;
  initialManualPin: AddressCoordinates | null;
  lookupPin: AddressCoordinates | null;
  manualPin: AddressCoordinates | null;
};

export type SiteLocationEditorAddress = {
  lookupCoordinates?: AddressCoordinatePair;
  manualCoordinates?: AddressCoordinatePair;
};

export type SiteLocationEditorAction =
  | { type: 'closed' }
  | {
      type: 'addressSynced';
      addressId: string;
      address: SiteLocationEditorAddress;
    }
  | { type: 'pinMoved'; coordinates: AddressCoordinatePair }
  | { type: 'pinReset' };

export const CLOSED_EDITOR_STATE: SiteLocationEditorState = {
  addressId: null,
  initialManualPin: null,
  lookupPin: null,
  manualPin: null,
};

const coordinatesAreEqual = (
  first: AddressCoordinates | null,
  second: AddressCoordinates | null
) =>
  first?.latitude === second?.latitude &&
  first?.longitude === second?.longitude;

export const getIsDirty = (state: SiteLocationEditorState) =>
  !coordinatesAreEqual(state.manualPin, state.initialManualPin);

export const getInitialEditorState = (
  addressId: string,
  address: SiteLocationEditorAddress
): SiteLocationEditorState => {
  const manualPin = getAddressCoordinates(address.manualCoordinates);

  return {
    addressId,
    initialManualPin: manualPin,
    lookupPin: getAddressCoordinates(address.lookupCoordinates),
    manualPin,
  };
};

export const siteLocationEditorReducer = (
  state: SiteLocationEditorState,
  action: SiteLocationEditorAction
): SiteLocationEditorState => {
  switch (action.type) {
    case 'closed':
      // Do not carry an unsaved pin into the next opening of the editor.
      return CLOSED_EDITOR_STATE;
    case 'addressSynced': {
      const refreshed = getInitialEditorState(action.addressId, action.address);
      if (state.addressId !== action.addressId || !getIsDirty(state)) {
        return refreshed;
      }

      // The address query may refresh while the editor is open. Keep a dirty
      // manual draft, but always let the lookup marker follow the latest result.
      return { ...state, lookupPin: refreshed.lookupPin };
    }
    case 'pinMoved': {
      const coordinates = getAddressCoordinates(action.coordinates);
      return coordinates ? { ...state, manualPin: coordinates } : state;
    }
    case 'pinReset':
      return { ...state, manualPin: null };
  }
};

export const getEditorPin = (
  manualCoordinates: AddressCoordinatePair,
  lookupCoordinates: AddressCoordinatePair
): AddressCoordinates | null =>
  getAddressCoordinates(manualCoordinates) ??
  getAddressCoordinates(lookupCoordinates);

export const getDraggedPosition = (
  originalPosition: AddressCoordinates,
  dragStart: AddressCoordinates | null | undefined,
  dragEnd: AddressCoordinates | null | undefined
): AddressCoordinates | null => {
  if (!dragStart || !dragEnd) return null;

  return {
    latitude: originalPosition.latitude + dragEnd.latitude - dragStart.latitude,
    longitude:
      originalPosition.longitude + dragEnd.longitude - dragStart.longitude,
  };
};

export const getLookupGhost = (
  manualCoordinates: AddressCoordinatePair,
  lookupCoordinates: AddressCoordinatePair
): AddressCoordinates | null => {
  const manual = getAddressCoordinates(manualCoordinates);
  const lookup = getAddressCoordinates(lookupCoordinates);

  if (!manual || !lookup) return null;
  return getDistance(manual, lookup) > LOOKUP_MARKER_COLOCATION_TOLERANCE_METRES
    ? lookup
    : null;
};

export const getDistanceBetweenCoordinatesInMetres = (
  first: AddressCoordinates,
  second: AddressCoordinates
) => getDistance(first, second);

export const formatList = (values: readonly string[]) => {
  if (values.length < 2) return values[0] ?? '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')} and ${values.at(-1)}`;
};

export const getNonUpdatingIntegrationNames = (services: readonly string[]) => {
  const names: string[] = [];

  for (const service of new Set(services)) {
    if ((CREATE_ONLY_DESIGN_SERVICES as readonly string[]).includes(service)) {
      names.push(
        readableProjectIntegrationService[service as ProjectIntegrationService]
      );
    }
  }

  return names;
};
